import { colors, ui } from '@beelday/common'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { FC, ReactNode } from 'react'
import { DESKTOP_WIDE_HEADING, LobbySize, SCALE } from './lobby-size'

export const LOBBY_HEADING_ID = 'in-person-lobby-heading'

const surface = {
	phone: css`
		padding: 20px;
		gap: 20px;
		border: 1px solid ${colors.borderLightGray};
	`,
	desktop: css`
		max-width: 1040px;
		padding: 32px;
		gap: 24px;
		box-shadow: 0 12px 40px ${colors.darkIndigo15};

		${ui.responsive.desktop} {
			padding: 40px;
			gap: 40px;
		}
	`,
	room: css`
		max-width: 1480px;
		padding: 56px;
		gap: 40px;
		box-shadow: 0 12px 40px ${colors.darkIndigo15};

		${ui.responsive.desktop} {
			padding: 72px;
			gap: 72px;
		}
	`,
}

/**
 * The card: the illustration beside the words from 1280px up, above them on
 * anything narrower. A phone always stacks.
 */
const Card = styled.section<{ $size: LobbySize }>`
	box-sizing: border-box;
	width: 100%;
	margin: auto;
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	align-items: center;
	background: ${colors.white};
	border-radius: 16px;
	${props => surface[props.$size]}

	${ui.responsive.desktop} {
		${props =>
			props.$size === 'phone'
				? ''
				: 'grid-template-columns: minmax(0, 45fr) minmax(0, 55fr);'}
	}
`

const Figure = styled.figure`
	margin: 0;
	display: flex;
	justify-content: center;
`

const Illustration = styled.img<{ $size: LobbySize }>`
	display: block;
	width: 100%;
	height: auto;
	max-height: ${props => (props.$size === 'phone' ? 200 : 220)}px;
	object-fit: contain;
	user-select: none;

	${ui.responsive.desktop} {
		max-height: none;
	}
`

const Content = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
`

/** The kind of moment, above the heading: a mint dash and a word. */
export const LobbyEyebrow = styled.div<{ $size: LobbySize }>`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: ${colors.indigoBlue};
	font-size: ${props => SCALE[props.$size].small}px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;

	&::before {
		content: '';
		flex: none;
		width: 24px;
		height: 4px;
		border-radius: 2px;
		background: ${colors.greenishCyan};
	}
`

const Heading = styled.h1<{ $size: LobbySize }>`
	margin: 0;
	color: ${colors.indigoBlue};
	font-size: ${props => SCALE[props.$size].heading}px;
	font-weight: 700;
	line-height: 1.15;
	overflow-wrap: anywhere;

	${ui.responsive.desktop} {
		${props =>
			props.$size === 'desktop' ? `font-size: ${DESKTOP_WIDE_HEADING}px;` : ''}
	}
`

const Meta = styled.p<{ $size: LobbySize }>`
	margin: 0;
	display: flex;
	flex-wrap: wrap;
	gap: 4px 12px;
	color: ${colors.brightBlackish};
	font-size: ${props => SCALE[props.$size].meta}px;
	line-height: 1.4;

	& > span + span::before {
		content: '·';
		margin-right: 12px;
		color: ${colors.heather};
	}
`

const Subject = styled.span`
	color: ${colors.blackish};
	font-weight: 500;
	overflow-wrap: anywhere;
`

const Hours = styled.span`
	font-variant-numeric: tabular-nums;
`

type Props = {
	size: LobbySize
	/** Decorative: the words carry the meaning. */
	illustration: string
	eyebrow: ReactNode
	heading: ReactNode
	/** The training's name. */
	subject?: string
	/** The day's planned hours, already formatted. */
	hours?: string
	/** Lines under the heading: who leads, when it starts. */
	facts?: ReactNode
	/** Steps, the status, the action. */
	children?: ReactNode
	testId?: string
}

/**
 * The lobby's one card: a picture of the moment (decorative), then the
 * eyebrow, the heading - the screen's one `h1` - the training, and whatever
 * the moment and the role add underneath.
 */
export const LobbyHero: FC<Props> = ({
	size,
	illustration,
	eyebrow,
	heading,
	subject,
	hours,
	facts,
	children,
	testId,
}) => (
	<Card $size={size} aria-labelledby={LOBBY_HEADING_ID} data-testid={testId}>
		<Figure>
			<Illustration
				$size={size}
				src={illustration}
				alt=""
				aria-hidden="true"
				draggable={false}
			/>
		</Figure>
		<Content>
			<LobbyEyebrow $size={size}>{eyebrow}</LobbyEyebrow>
			<Heading $size={size} id={LOBBY_HEADING_ID}>
				{heading}
			</Heading>
			{subject || hours ? (
				<Meta $size={size}>
					{subject ? <Subject>{subject}</Subject> : null}
					{hours ? <Hours>{hours}</Hours> : null}
				</Meta>
			) : null}
			{facts}
			{children}
		</Content>
	</Card>
)
